import React, { useState, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
} from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useResponsive } from '../../hooks/useResponsive';
import { FONTS } from '../../constants/config';
import { createShadow } from '../../utils/styles';
import Toast from '../Toast';
import { CalendarEvent, CalendarDay, CalendarView } from './types';
import CalendarHeader, { DateRange } from './CalendarHeader';
import WeeklyGrid from './WeeklyGrid';
import DayView from './DayView';
import MonthlyGrid from './MonthlyGrid';
import CalendarSkeleton from './CalendarSkeleton';

interface ActivityCalendarProps {
  events: CalendarEvent[];
  initialDate?: Date;
  startHour?: number;
  endHour?: number;
  onEventPress?: (event: CalendarEvent) => void;
  onNewActivity?: () => void;
  hideNewButton?: boolean;
  isLoading?: boolean;
  titleOverride?: string;
}

/** Arabic short day names matching the Figma screenshot */
const AR_DAY_NAMES = ['الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت', 'الأحد'];
const EN_DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MONTH_NAMES_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTH_NAMES_SHORT_EN = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const MONTH_NAMES_AR = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];

/** Return the Monday (ISO week start) for a given date */
function getWeekMonday(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function toISODateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function ActivityCalendar({
  events,
  initialDate,
  startHour: startHourProp = 7,
  endHour: endHourProp = 14,
  onEventPress,
  onNewActivity,
  hideNewButton = false,
  isLoading = false,
  titleOverride,
}: ActivityCalendarProps) {
  const { colors } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const { isMobile } = useResponsive();

  // Auto-expand hour range to fit all events (with 1-hour padding)
  const { startHour, endHour } = useMemo(() => {
    let minH = startHourProp;
    let maxH = endHourProp;
    for (const e of events) {
      const [sh, sm] = e.startTime.split(':').map(Number);
      const [eh, em] = e.endTime.split(':').map(Number);
      const s = sh + sm / 60;
      const end = eh + em / 60;
      if (s < minH) minH = Math.floor(s);
      if (end > maxH) maxH = Math.ceil(end);
    }
    return { startHour: Math.max(0, minH - 1), endHour: Math.min(24, maxH + 1) };
  }, [events, startHourProp, endHourProp]);

  const today = useMemo(() => new Date(), []);
  const [currentDate, setCurrentDate] = useState<Date>(initialDate ?? today);
  const [activeView, setActiveView] = useState<CalendarView>('weekly');
  const [filterRange, setFilterRange] = useState<DateRange | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // On mobile, force single-day view; on desktop, use the active view tab
  const effectiveView: CalendarView = isMobile ? 'daily' : activeView;

  // Build the week's CalendarDay objects (used by weekly view)
  const weekDays: CalendarDay[] = useMemo(() => {
    // When a filter range is active, start from currentDate directly;
    // otherwise start from the Monday of the current week
    const weekStart = filterRange ? new Date(currentDate) : getWeekMonday(currentDate);
    const dayNames = isRTL ? AR_DAY_NAMES : EN_DAY_NAMES;

    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      const dateStr = toISODateString(d);
      const dayIndex = (d.getDay() + 6) % 7; // 0=Mon

      // Only include events within the filter range if set
      const dayEvents = events.filter((e) => {
        if (e.date !== dateStr) return false;
        if (filterRange) {
          const startStr = toISODateString(filterRange.start);
          const endStr = toISODateString(filterRange.end);
          return e.date >= startStr && e.date <= endStr;
        }
        return true;
      });

      return {
        date: d,
        dayName: dayNames[dayIndex],
        dayNumber: d.getDate(),
        isToday: isSameDay(d, today),
        events: dayEvents,
      };
    });
  }, [currentDate, events, isRTL, filterRange, today]);

  // Current day data for daily view
  const currentDayData: CalendarDay = useMemo(() => {
    const match = weekDays.find((d) => isSameDay(d.date, currentDate));
    if (match) return match;
    // fallback: build a single-day entry
    const dayIndex = (currentDate.getDay() + 6) % 7; // 0=Mon
    const dayNames = isRTL ? AR_DAY_NAMES : EN_DAY_NAMES;
    const dateStr = toISODateString(currentDate);
    return {
      date: currentDate,
      dayName: dayNames[dayIndex],
      dayNumber: currentDate.getDate(),
      isToday: true,
      events: events.filter((e) => e.date === dateStr),
    };
  }, [currentDate, weekDays, events, isRTL, today]);

  // Show filter range if set, otherwise today's date
  const dateRangeLabel = useMemo(() => {
    const months = isRTL ? MONTH_NAMES_AR : MONTH_NAMES_EN;

    if (filterRange) {
      const { start, end } = filterRange;
      if (isSameDay(start, end)) {
        // Single day
        if (isRTL) return `${start.getDate()} ${months[start.getMonth()]} ${start.getFullYear()}`;
        return `${months[start.getMonth()]} ${start.getDate()}, ${start.getFullYear()}`;
      }
      // Range
      if (start.getMonth() === end.getMonth()) {
        if (isRTL) return `${start.getDate()} - ${end.getDate()} ${months[start.getMonth()]} ${start.getFullYear()}`;
        return `${months[start.getMonth()]} ${start.getDate()} - ${end.getDate()}, ${start.getFullYear()}`;
      }
      if (isRTL) return `${start.getDate()} ${months[start.getMonth()]} - ${end.getDate()} ${months[end.getMonth()]} ${end.getFullYear()}`;
      return `${months[start.getMonth()]} ${start.getDate()} - ${months[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
    }

    // Default: current selected date
    const d = currentDate;
    if (isRTL) return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  }, [currentDate, isRTL, filterRange]);

  // Navigation — moves by week in weekly view, by 1 day otherwise
  const handlePrev = useCallback(() => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() - (effectiveView === 'weekly' ? 7 : 1));
      return d;
    });
  }, [effectiveView]);

  const handleNext = useCallback(() => {
    setCurrentDate((prev) => {
      const d = new Date(prev);
      d.setDate(d.getDate() + (effectiveView === 'weekly' ? 7 : 1));
      return d;
    });
  }, [effectiveView]);

  const handleToday = useCallback(() => {
    setCurrentDate(new Date());
    setFilterRange(null);
  }, []);

  const showComingSoon = useCallback(() => {
    setToastMessage(t('activityCalendar.comingSoon'));
    setToastVisible(true);
  }, [t]);

  const handleSelectRange = useCallback((range: DateRange) => {
    setFilterRange(range);
    setCurrentDate(range.start);
  }, []);

  // All three views are functional — switch freely
  const handleViewChange = useCallback((view: CalendarView) => {
    setActiveView(view);
  }, []);

  if (isLoading) {
    return <CalendarSkeleton />;
  }

  return (
    <View
      style={[
        styles.container,
        {
          backgroundColor: colors.card,
          borderColor: colors.cardBorder,
          ...createShadow(0, 1, 3, '#000', 0.08),
        },
      ]}
    >
      <CalendarHeader
        dateRangeLabel={dateRangeLabel}
        activeView={activeView}
        onViewChange={handleViewChange}
        onToday={handleToday}
        onPrev={handlePrev}
        onNext={handleNext}
        onSelectRange={handleSelectRange}
        onNewActivity={onNewActivity || showComingSoon}
        titleOverride={titleOverride}
        hideNewButton={hideNewButton || !onNewActivity}
      />

      {effectiveView === 'weekly' && (
        <WeeklyGrid
          days={weekDays}
          startHour={startHour}
          endHour={endHour}
          onEventPress={onEventPress}
        />
      )}

      {effectiveView === 'daily' && (
        <DayView
          day={currentDayData}
          startHour={startHour}
          endHour={endHour}
          onEventPress={onEventPress}
        />
      )}

      {effectiveView === 'monthly' && (
        <MonthlyGrid
          currentDate={currentDate}
          events={events}
          onEventPress={onEventPress}
        />
      )}

      {/* Empty state — weekly */}
      {effectiveView === 'weekly' &&
        weekDays.every((d) => d.events.length === 0) && (
          <View style={styles.emptyState}>
            <Text style={[styles.emptyText, { color: colors.textMuted }]}>
              {t('activityCalendar.noEvents')}
            </Text>
          </View>
        )}

      {/* Empty state — daily */}
      {effectiveView === 'daily' && currentDayData.events.length === 0 && (
        <View style={styles.emptyState}>
          <Text style={[styles.emptyText, { color: colors.textMuted }]}>
            {t('activityCalendar.noEventsDay')}
          </Text>
        </View>
      )}

      <Toast
        visible={toastVisible}
        message={toastMessage}
        type="info"
        onHide={() => setToastVisible(false)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    overflow: 'visible',
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
  },
  emptyText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    lineHeight: 20,
  },
});
